import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';
import { EmailService } from '../notifications/email.service';
import { LocalBackupService } from './local-backup.service';
import { backupBucket, backupEnvPrefix } from './backup-storage.constants';

/** Where backup reports go. Defaults to the connectivity check's recipient. */
export const BACKUP_REPORT_EMAIL =
  process.env.BACKUP_REPORT_EMAIL ?? process.env.HEALTHCHECK_ALERT_EMAIL ?? 'sudharshanreddyjanike@gmail.com';

/**
 * `always` reports every run; `failures` reports only the ones that failed.
 *
 * Three policies are enabled — a 4-hourly dump, a nightly base backup and a
 * nightly snapshot — so `always` is about eight mails a day. That is a
 * deliberate choice rather than an oversight: a backup system that only ever
 * writes when something breaks is indistinguishable from a backup system that
 * has stopped running, which is precisely how this deployment went weeks
 * without anyone noticing WAL was never archiving.
 */
export const BACKUP_REPORT_MODE = (process.env.BACKUP_REPORT_MODE ?? 'always') as 'always' | 'failures';

/**
 * When the WAL digest goes out. 08:05, just after the connectivity check at
 * 08:00, so the morning mail arrives as a pair rather than scattered.
 */
export const WAL_DIGEST_CRON = process.env.WAL_DIGEST_CRON ?? '5 8 * * *';

/**
 * Pinned, because the server does not run on India time.
 *
 * The backup policies' own `timezone` column is not honoured by the scheduler —
 * their cron expressions run in the server's local zone — so "01:00" on that
 * box is not 01:00 IST. This cron at least says which zone it means.
 */
export const WAL_DIGEST_TIMEZONE = process.env.WAL_DIGEST_TZ ?? 'Asia/Kolkata';

const CELL = 'padding:6px 10px;border-bottom:1px solid #e5e7eb';

/**
 * Email what the backup subsystem actually did.
 *
 * ── WHY PER-RUN, AND WHY WAL IS DIFFERENT ────────────────────────────────────
 *
 * A backup run is an event: it happens a handful of times a day, it either
 * worked or it did not, and when it did not the error text is the whole point.
 * So each run gets its own mail carrying the full error, untruncated.
 *
 * WAL is not an event. Segments arrive every few minutes, and a mail per
 * segment would be ~96 a day of pure noise that would bury the run reports
 * beside them. What matters about WAL is the SHAPE of a day: how many segments
 * arrived, whether there are gaps, how far behind the archive is, and how much
 * of the last day is genuinely recoverable. That is a daily digest.
 */
@Injectable()
export class BackupReportService {
  private readonly logger = new Logger(BackupReportService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly email: EmailService,
    private readonly local: LocalBackupService
  ) {}

  /**
   * Report one finished run.
   *
   * Never throws. A backup that succeeded must not be recorded as failed
   * because the mail server was down, and a backup that failed must not have
   * its real error replaced by an SMTP error.
   */
  async sendRunReport(runId: string): Promise<void> {
    try {
      const run = await this.prisma.backupRun.findUnique({
        where: { id: runId },
        include: { policy: true, repository: true },
      });
      if (!run) return;

      const failed = run.status !== 'completed';
      if (BACKUP_REPORT_MODE === 'failures' && !failed) return;

      const name = run.policy?.name ?? run.backupType;
      const env = backupEnvPrefix();

      await this.email.send({
        to: BACKUP_REPORT_EMAIL,
        subject: failed
          ? `[ForgeStack ${env}] Backup FAILED — ${name}`
          : `[ForgeStack ${env}] Backup OK — ${name}`,
        html: this.renderRun(run, failed),
        typeCode: 'backup_run_report',
      });
    } catch (err) {
      this.logger.warn(`could not email run report for ${runId}: ${(err as Error).message}`);
    }
  }

  @Cron(WAL_DIGEST_CRON, { name: 'wal-digest', timeZone: WAL_DIGEST_TIMEZONE })
  async sendWalDigest(): Promise<void> {
    try {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000);
      const blocked = this.local.walArchivingBlockedReason();

      const segments = await this.prisma.walArchiveSegment.findMany({
        where: { archivedAt: { gte: since } },
        orderBy: [{ timeline: 'asc' }, { segmentName: 'asc' }],
        select: {
          segmentName: true,
          timeline: true,
          archivedAt: true,
          uploadedAt: true,
          compressedBytes: true,
          status: true,
          lagSeconds: true,
        },
      });

      const uploaded = segments.filter((s) => s.status === 'uploaded');
      const bytes = uploaded.reduce((sum, s) => sum + Number(s.compressedBytes ?? 0), 0);
      const lags = uploaded.map((s) => s.lagSeconds ?? 0).filter((n) => n > 0);
      const newest = uploaded[uploaded.length - 1];

      const gaps: string[] = [];
      for (let i = 1; i < uploaded.length; i++) {
        if (!this.isConsecutive(uploaded[i - 1].segmentName, uploaded[i].segmentName)) {
          gaps.push(`${uploaded[i - 1].segmentName} → ${uploaded[i].segmentName}`);
        }
      }

      // The number that actually matters: how much of the recent past exists
      // only on the database's own disk.
      const unprotectedMinutes = newest?.archivedAt
        ? Math.round((Date.now() - newest.archivedAt.getTime()) / 60000)
        : null;

      const healthy = !blocked && uploaded.length > 0 && gaps.length === 0;

      await this.email.send({
        to: BACKUP_REPORT_EMAIL,
        subject: healthy
          ? `[ForgeStack ${backupEnvPrefix()}] WAL OK — ${uploaded.length} segment(s) in 24h`
          : `[ForgeStack ${backupEnvPrefix()}] WAL PROBLEM — ${
              blocked ? 'archiving is off' : gaps.length ? `${gaps.length} gap(s)` : 'no segments archived'
            }`,
        html: this.renderWalDigest({
          blocked,
          total: segments.length,
          uploaded: uploaded.length,
          bytes,
          gaps,
          unprotectedMinutes,
          newestSegment: newest?.segmentName ?? null,
          newestAt: newest?.archivedAt ?? null,
          medianLagSeconds: lags.length ? lags.sort((a, b) => a - b)[Math.floor(lags.length / 2)] : null,
        }),
        typeCode: 'wal_daily_digest',
      });

      this.logger.log(
        `WAL digest sent: ${uploaded.length} segment(s), ${gaps.length} gap(s)` +
          (blocked ? ` — archiving blocked: ${blocked}` : '')
      );
    } catch (err) {
      this.logger.warn(`could not send WAL digest: ${(err as Error).message}`);
    }
  }

  // ── Rendering ──────────────────────────────────────────────────────────────

  private renderRun(run: Record<string, unknown>, failed: boolean): string {
    const policy = run['policy'] as Record<string, unknown> | null;
    const started = run['startedAt'] as Date | null;
    const finished = run['finishedAt'] as Date | null;
    const size = Number(run['sizeBytes'] ?? 0);
    const raw = Number(run['repoSizeBytes'] ?? 0);

    const rows: Array<[string, string]> = [
      ['Policy', String(policy?.['name'] ?? run['backupType'])],
      ['Type', String(run['backupType'])],
      ['Trigger', String(run['trigger'])],
      ['Status', String(run['status'])],
      ['Started', started ? started.toISOString() : '—'],
      ['Finished', finished ? finished.toISOString() : '—'],
      ['Duration', run['durationMs'] ? `${Math.round(Number(run['durationMs']) / 1000)} s` : '—'],
      ['Stored size', size ? formatBytes(size) : '—'],
      ['Bucket', backupBucket() ?? '—'],
      ['Path', String(run['manifestKey'] ?? '—')],
    ];

    // The compression ratio is the number that decides whether a 1 GB budget
    // holds, so it is shown rather than left to be worked out.
    if (size && raw && raw > size) {
      rows.push(['Compression', `${formatBytes(raw)} → ${formatBytes(size)} (${(raw / size).toFixed(1)}:1)`]);
    }

    const error = run['error'] ? String(run['error']) : null;

    return `
      <div style="font-family:system-ui,-apple-system,Segoe UI,sans-serif;max-width:760px">
        <h2 style="margin:0 0 12px;color:${failed ? '#b91c1c' : '#047857'}">
          Backup ${failed ? 'FAILED' : 'completed'}
        </h2>
        <table style="border-collapse:collapse;width:100%;font-size:14px">
          ${rows
            .map(
              ([label, value]) =>
                `<tr><td style="${CELL};color:#6b7280;width:150px">${label}</td>` +
                `<td style="${CELL};font-family:monospace">${escapeHtml(value)}</td></tr>`
            )
            .join('')}
        </table>
        ${
          error
            ? `<h3 style="margin:20px 0 6px;color:#b91c1c">Error</h3>
               <pre style="background:#fef2f2;border:1px solid #fecaca;border-radius:6px;padding:12px;
                           white-space:pre-wrap;word-break:break-word;font-size:13px;color:#7f1d1d">${escapeHtml(
                             error
                           )}</pre>`
            : ''
        }
      </div>`;
  }

  private renderWalDigest(d: {
    blocked: string | null;
    total: number;
    uploaded: number;
    bytes: number;
    gaps: string[];
    unprotectedMinutes: number | null;
    newestSegment: string | null;
    newestAt: Date | null;
    medianLagSeconds: number | null;
  }): string {
    if (d.blocked) {
      return `
        <div style="font-family:system-ui,-apple-system,Segoe UI,sans-serif;max-width:760px">
          <h2 style="margin:0 0 8px;color:#b91c1c">WAL archiving is not running</h2>
          <p style="margin:0 0 12px;color:#374151">
            No point-in-time recovery is possible. Base backups still restore to the instant they
            were taken, but nothing between them can be recovered — everything since the last one
            exists only on the database's own disk.
          </p>
          <pre style="background:#fef2f2;border:1px solid #fecaca;border-radius:6px;padding:12px;
                      white-space:pre-wrap;font-size:13px;color:#7f1d1d">${escapeHtml(d.blocked)}</pre>
          <p style="margin:12px 0 0;color:#6b7280;font-size:13px">
            Setup: <code>ops/database/postgresql.local-archive.conf</code>
          </p>
        </div>`;
    }

    const rows: Array<[string, string]> = [
      ['Segments archived', `${d.uploaded} of ${d.total} in the last 24h`],
      ['Stored', formatBytes(d.bytes)],
      ['Newest segment', d.newestSegment ?? '—'],
      ['Newest archived at', d.newestAt ? d.newestAt.toISOString() : '—'],
      ['Median upload lag', d.medianLagSeconds === null ? '—' : `${d.medianLagSeconds} s`],
      [
        'Not yet in R2',
        d.unprotectedMinutes === null
          ? 'unknown'
          : `${d.unprotectedMinutes} min of changes — still in the open segment on the server`,
      ],
    ];

    return `
      <div style="font-family:system-ui,-apple-system,Segoe UI,sans-serif;max-width:760px">
        <h2 style="margin:0 0 12px;color:${d.gaps.length ? '#b45309' : '#047857'}">
          WAL archive — last 24 hours
        </h2>
        <table style="border-collapse:collapse;width:100%;font-size:14px">
          ${rows
            .map(
              ([label, value]) =>
                `<tr><td style="${CELL};color:#6b7280;width:180px">${label}</td>` +
                `<td style="${CELL}">${escapeHtml(value)}</td></tr>`
            )
            .join('')}
        </table>
        ${
          d.gaps.length
            ? `<h3 style="margin:20px 0 6px;color:#b45309">Gaps</h3>
               <p style="margin:0 0 8px;color:#374151;font-size:14px">
                 Replay applies segments in strict order and cannot skip one, so recovery stops at
                 the first gap. Anything after it is unreachable.
               </p>
               <ul style="font-family:monospace;font-size:13px;color:#7c2d12">
                 ${d.gaps.map((g) => `<li>${escapeHtml(g)}</li>`).join('')}
               </ul>`
            : ''
        }
      </div>`;
  }

  /**
   * Segment counter runs 00..FE within a logical file (Postgres skips FF), then
   * the logical file increments and the counter restarts.
   */
  private isConsecutive(a: string, b: string): boolean {
    if (a.length !== 24 || b.length !== 24 || a.slice(0, 8) !== b.slice(0, 8)) return false;
    const logA = parseInt(a.slice(8, 16), 16);
    const segA = parseInt(a.slice(16, 24), 16);
    const logB = parseInt(b.slice(8, 16), 16);
    const segB = parseInt(b.slice(16, 24), 16);
    if (logB === logA && segB === segA + 1) return true;
    return logB === logA + 1 && segA === 0xfe && segB === 0x00;
  }
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
